import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

const metadata: Record<string, [string, string]> = {
  '/': [
    'LAVYN | AI Study Tutor for School Learners',
    'Get AI study help in Mathematics, Physical Science, Life Sciences and Accounting. Ask questions, upload schoolwork and explore study tools with LAVYN.',
  ],
  '/about': [
    'About LAVYN | AI Study Tutor',
    'Learn about LAVYN, an AI study tutor for Mathematics, Physical Science, Life Sciences and Accounting, with question help and study tools.',
  ],
  '/contact': ['Contact LAVYN', 'Email support@lavyntutor.com for LAVYN account help, technical support or privacy requests.'],
  '/terms': ['Terms of Service | LAVYN', 'Rules for LAVYN accounts, AI tutoring, learning content, plan limits, Premium subscriptions and service availability.'],
  '/privacy': ['Privacy Policy | LAVYN', 'How LAVYN processes account information, learning content, AI tutoring, browser storage and advertising technologies.'],
  '/history': ['Study history', 'Review your LAVYN study history.'],
  '/ai-assistant': ['Tutor', 'Ask follow-up questions and explore study topics with LAVYN Tutor.'],
  '/profile': ['Profile & Settings', 'Manage your LAVYN account, preferences, and current access.'],
  '/profile/premium': ['Premium offer preview', 'View the LAVYN Premium offer preview. Checkout is not available.'],
  '/auth/login': ['Sign in', 'Sign in to your LAVYN account.'],
  '/auth/signup': ['Create an account', 'Create a LAVYN account.'],
  '/auth/forgot-password': ['Forgot password', 'Request a password reset for your LAVYN account.'],
  '/auth/reset-password': ['Reset password', 'Reset your LAVYN account password.'],
  '/scan/start': ['Snap & Solve', 'Start a schoolwork question in LAVYN.'],
  '/scan/camera': ['Capture schoolwork', 'Take a photo of your schoolwork question.'],
  '/scan/analyzing': ['Preparing your explanation', 'LAVYN is preparing your study explanation.'],
  '/scan/output': ['Choose your explanation', 'Choose an output for your schoolwork question.'],
  '/scan/result': ['Study explanation', 'Review your LAVYN explanation.'],
  '/scan/result-text': ['Text explanation', 'Read your LAVYN study explanation.'],
  '/scan/result-video': ['Narrated lesson', 'Watch your LAVYN AI-narrated study lesson.'],
};

const canonicalOrigin = 'https://lavyntutor.com';
const indexablePublicPaths = new Set(['/', '/about']);

function setMeta(name: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.name = name;
    document.head.appendChild(element);
  }
  element.content = content;
}

function setPropertyMeta(property: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute('property', property);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonical(path: string | null) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!path) {
    element?.remove();
    return;
  }
  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.appendChild(element);
  }
  element.href = `${canonicalOrigin}${path === '/' ? '/' : path}`;
}

export default function RouteMetadata() {
  const { pathname, search } = useLocation();
  useLayoutEffect(() => {
    const normalized = pathname.replace(/\/+$/, '') || '/';
    const dynamic = normalized.startsWith('/lesson/') ? ['Lesson', 'Review your LAVYN study lesson.']
      : normalized.startsWith('/subject/') ? ['Subject', 'Explore a subject in LAVYN.']
      : normalized.startsWith('/topic/') ? ['Topic', 'Explore a study topic in LAVYN.']
      : ['Page not found', 'This LAVYN page could not be found.'];
    const [title, description] = metadata[normalized] ?? dynamic;
    const tool = normalized === '/' ? new URLSearchParams(search).get('tool') : null;
    const knownTools = ['Practice', 'Study Cards', 'Quick Learn', 'Smart Calculator'];
    const pageTitle = tool && knownTools.includes(tool) ? `${tool} | LAVYN` : title;
    const indexable = indexablePublicPaths.has(normalized);
    document.title = pageTitle;
    setMeta('description', description);
    setMeta('robots', indexable ? 'index, follow' : 'noindex, nofollow');
    setCanonical(indexable ? normalized : null);
    if (indexable) {
      setPropertyMeta('og:type', 'website');
      setPropertyMeta('og:site_name', 'LAVYN');
      setPropertyMeta('og:url', `${canonicalOrigin}${normalized === '/' ? '/' : normalized}`);
      setPropertyMeta('og:title', pageTitle);
      setPropertyMeta('og:description', description);
    }
  }, [pathname, search]);
  return null;
}
